import { createElement, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronsDownUp, ChevronsUpDown, Users } from "lucide-react";
import { Badge, LEVEL_LABELS, fmtDate } from "./ui";

const initials = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const LEVEL_COLORS = ["bg-ink", "bg-leaf-deep", "bg-[#2b6cb0]", "bg-[#8247E5]", "bg-[#b7791f]", "bg-coral"];

const buildTree = (root, network) => {
  const byParent = {};
  network.forEach((n) => {
    if (!byParent[n.sponsor_id]) byParent[n.sponsor_id] = [];
    byParent[n.sponsor_id].push(n);
  });
  const attach = (node) => ({ ...node, children: (byParent[node.dist_id] || []).map(attach) });
  return attach({ ...root, level: 0 });
};

const countDesc = (node) => node.children.reduce((a, c) => a + 1 + countDesc(c), 0);

const Node = ({ node, expanded, toggle, selected, select, highlight, isLast }) => {
  const open = expanded.has(node.dist_id);
  const hasKids = node.children.length > 0;
  const hit = highlight && `${node.name} ${node.dist_id}`.toLowerCase().includes(highlight);
  return (
    <li className="relative pl-7" data-testid={`tree-node-${node.dist_id}`}>
      {node.level > 0 && (
        <>
          <span className={`absolute left-0 top-0 w-px bg-ink/15 ${isLast ? "h-7" : "h-full"}`} />
          <span className="absolute left-0 top-7 h-px w-7 bg-ink/15" />
        </>
      )}
      <div
        onClick={() => select(node)}
        className={`relative my-1.5 flex w-fit max-w-full cursor-pointer items-center gap-3 rounded-2xl border bg-white py-2 pl-2 pr-3 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${
          selected?.dist_id === node.dist_id ? "border-ink shadow-[0_8px_24px_-12px_rgba(10,18,48,0.4)]" : hit ? "border-sun" : "border-ink/10"
        }`}
        data-testid={`tree-card-${node.dist_id}`}
      >
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl font-display text-sm font-semibold text-white ${LEVEL_COLORS[node.level]}`}>{initials(node.name)}</span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight">{node.name}</p>
          <p className="font-mono text-[11px] text-ink/60">
            ID {node.dist_id} · {node.level === 0 ? "Tú" : LEVEL_LABELS[node.level]}
            {hasKids && ` · ${node.children.length} directo${node.children.length > 1 ? "s" : ""}`}
          </p>
        </div>
        <span className={`ml-1 h-2.5 w-2.5 shrink-0 rounded-full ${node.membership_status === "activa" ? "bg-leaf" : "bg-sun"}`} title={node.membership_status === "activa" ? "Membresía activa" : "Pendiente de pago"} />
        {hasKids && (
          <button
            onClick={(e) => { e.stopPropagation(); toggle(node.dist_id); }}
            className="ml-1 grid h-7 w-7 shrink-0 place-items-center rounded-full border border-ink/10 hover:bg-paper"
            aria-label={open ? "Contraer" : "Expandir"}
            data-testid={`tree-toggle-${node.dist_id}`}
          >
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {hasKids && open && (
          <motion.ul initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
            {node.children.map((c, i) =>
              createElement(Node, { key: c.dist_id, node: c, expanded, toggle, selected, select, highlight, isLast: i === node.children.length - 1 })
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
};

const Detail = ({ node, onClose }) => (
  <motion.aside initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} className="rounded-2xl border border-ink/10 bg-white p-5" data-testid="tree-detail">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink/60">ID {node.dist_id}</p>
        <p className="mt-1 font-display text-lg font-semibold leading-tight">{node.name}</p>
      </div>
      <button onClick={onClose} className="text-xs text-ink/60 hover:text-ink" data-testid="tree-detail-close">Cerrar</button>
    </div>
    <dl className="mt-4 space-y-2 text-sm">
      <div className="flex justify-between gap-3"><dt className="text-ink/60">Nivel</dt><dd className="font-semibold">{node.level === 0 ? "Tú (raíz)" : LEVEL_LABELS[node.level]}</dd></div>
      <div className="flex justify-between gap-3"><dt className="text-ink/60">País</dt><dd>{node.country}</dd></div>
      <div className="flex justify-between gap-3"><dt className="text-ink/60">Patrocinador</dt><dd className="font-mono text-xs">{node.sponsor_id || "—"}</dd></div>
      <div className="flex justify-between gap-3"><dt className="text-ink/60">Membresía</dt><dd><Badge status={node.membership_status} label={node.membership_status === "activa" ? "Activa" : "Pendiente"} /></dd></div>
      <div className="flex justify-between gap-3"><dt className="text-ink/60">Registro</dt><dd>{fmtDate(node.created_at)}</dd></div>
      <div className="flex justify-between gap-3"><dt className="text-ink/60">Directos</dt><dd className="font-semibold">{node.children.length}</dd></div>
      <div className="flex justify-between gap-3"><dt className="text-ink/60">Total en su rama</dt><dd className="font-semibold">{countDesc(node)}</dd></div>
    </dl>
  </motion.aside>
);

export const GenealogyTree = ({ root, network, search = "" }) => {
  const tree = useMemo(() => buildTree(root, network), [root, network]);
  const allIds = useMemo(() => network.filter((n) => network.some((m) => m.sponsor_id === n.dist_id)).map((n) => n.dist_id), [network]);
  const [expanded, setExpanded] = useState(() => new Set([root.dist_id, ...network.filter((n) => n.level === 1).map((n) => n.dist_id)]));
  const [selected, setSelected] = useState(null);

  const toggle = (id) => setExpanded((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (!network.length)
    return (
      <div className="mt-5 grid place-items-center rounded-2xl border border-dashed border-ink/20 bg-white px-6 py-14 text-center" data-testid="tree-empty">
        <Users className="h-8 w-8 text-ink/40" />
        <p className="mt-3 text-sm text-ink/70">Tu árbol crecerá aquí cuando alguien se registre con tu enlace o QR.</p>
      </div>
    );

  return (
    <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_300px]" data-testid="genealogy-tree">
      <div className="overflow-x-auto rounded-2xl border border-ink/10 bg-paper/60 p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <button onClick={() => setExpanded(new Set([root.dist_id, ...allIds]))} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 font-semibold hover:bg-paper" data-testid="tree-expand-all"><ChevronsUpDown className="h-3.5 w-3.5" /> Expandir todo</button>
          <button onClick={() => setExpanded(new Set([root.dist_id]))} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 font-semibold hover:bg-paper" data-testid="tree-collapse-all"><ChevronsDownUp className="h-3.5 w-3.5" /> Contraer todo</button>
          <span className="ml-auto flex items-center gap-3 text-ink/60"><span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-leaf" /> Activa</span><span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-full bg-sun" /> Pendiente</span></span>
        </div>
        <ul className="min-w-[320px]">
          <Node node={tree} expanded={expanded} toggle={toggle} selected={selected} select={setSelected} highlight={search.trim().toLowerCase()} isLast />
        </ul>
      </div>
      <div>
        {selected ? <Detail node={selected} onClose={() => setSelected(null)} /> : (
          <div className="rounded-2xl border border-dashed border-ink/20 bg-white p-5 text-sm text-ink/60" data-testid="tree-hint">Haz clic en cualquier persona del árbol para ver su detalle. Usa las flechas para expandir o contraer cada rama.</div>
        )}
      </div>
    </div>
  );
};
